import {
  refreshMessageBackendRequest,
  refreshMessageBackendRequestCompleted,
  replaceMessageEmbeddedImages,
  replaceMessageEmbeddedImagesBase64,
  selectFolder
} from '../actions/application';
import { updateCacheIfExist } from '../actions/messages';
import { backendRequest, backendRequestCompleted } from '../actions/application';
import {
  abortControllerWrappers,
  abortFetch,
  apiFetch,
  credentialsHeaders,
  toJson,
  toText
} from './fetch';
import { updateFolder } from '../actions/folders';
import { refreshMessage } from '../actions/application';
import { closeResetFolderMessagesCacheEventSource } from './message';
import base64url from 'base64url';
import { toApiProxyUrl, repairMessageMojibake } from '../utils/utils';

/**
 * Performs a BE request to read an embedded attachment (image) and replaces the e-mail content
 * with the image once it's received.
 *
 * @param dispatch
 * @param credentials
 * @param folder
 * @param message
 * @param signal
 * @param attachment
 * @private
 */

const AUTHENTICATION_FAILED = `199 - "[AUTHENTICATIONFAILED] Invalid credentials (Failure)"`;

function _readEmbeddedContent(
  dispatch,
  credentials,
  folder,
  message,
  signal,
  attachment,
  format = 'blob'
) {
  dispatch(backendRequest());
  apiFetch(toApiProxyUrl(attachment._links.download.href), {
    method: 'GET',
    headers: credentialsHeaders(credentials),
    signal: signal
  })
    .then(response => response.blob())
    .then(blob => {
      if (format === 'blob') {
        dispatch(replaceMessageEmbeddedImages(folder, message, attachment, blob));
      } else if (format === 'base64') {
        const reader = new FileReader();
        reader.onloadend = function() {
          const base64 = reader.result;
          dispatch(replaceMessageEmbeddedImagesBase64(folder, message, attachment, base64));
        };
        reader.onerror = function () {
        };
        reader.readAsDataURL(blob);
      }
    })
    .catch(error => {
      // Swallow abort errors to avoid unhandled promise rejections when requests are cancelled
      if (error && (error.name === 'AbortError' || error.code === 20)) {
        return;
      }
      // Optionally log other errors for debugging without breaking the app flow
      // console.error('Embedded content fetch error', error);
    })
    .finally(() => {
      dispatch(backendRequestCompleted());
    });
}

/**
 * Returns a function that will perform a BE request to read a message.
 *
 * @param dispatch
 * @param credentials
 * @param folder
 * @param message
 * @returns {function(): Promise<Response>}
 * @private
 */
function _readMessageRequest(dispatch, credentials, folder, message) {
  // Abort + new signal
  abortFetch(abortControllerWrappers.readMessageAbortController);
  abortControllerWrappers.readMessageAbortController = new AbortController();
  const signal = abortControllerWrappers.readMessageAbortController.signal;

  return () => {
    dispatch(backendRequest());
    dispatch(refreshMessageBackendRequest());
    return apiFetch(
      toApiProxyUrl(folder._links.message.href.replace('{messageId}', message.uid)),
      {
        method: 'GET',
        headers: credentialsHeaders(credentials),
        signal: signal
      }
    )
      .then(response => {
        dispatch(refreshMessageBackendRequestCompleted());
        return response;
      })
      .then(toJson)
      .then(repairMessageMojibake)
      .catch(error => {
        dispatch(refreshMessageBackendRequestCompleted());
        if (error && (error.name === 'AbortError' || error.code === 20)) {
          // Resolve as null on abort so downstream can safely ignore
          return null;
        }
        throw error;
      })
      .finally(() => {
        dispatch(backendRequestCompleted());
      });
  };
}

function _readMessageRawRequest(dispatch, credentials, folder, message) {
  return () => {
    dispatch(backendRequest());
    return apiFetch(
      toApiProxyUrl(folder._links.message.href.replace('{messageId}', `${+message.uid}/raw`)),
      {
        method: 'GET',
        headers: credentialsHeaders(credentials)
      }
    )
      .then(response => response)
      .then(responseText => {
        let existAuthentication = false;
        responseText.headers.forEach(x => {
          if (AUTHENTICATION_FAILED === x) {
            existAuthentication = true;
          }
        });
        if (existAuthentication) {
          return {unAuthorized: existAuthentication};
        }
        return toText(responseText);
      })
      .catch(() => {
        // PEC-8747: el 401 ya pasó por la renovación de apiFetch; no se navega a /login ni se cierra sesión
        throw Error();
      })
      .finally(() => {
        dispatch(backendRequestCompleted());
      });
  };
}

function _readMessageRawRequestByMessageId(dispatch, credentials, folder, message) {
  return () => {
    dispatch(backendRequest());
    return apiFetch(
      toApiProxyUrl(folder._links.message.href.replace('{messageId}', `${message.messageId}/searchMessageById`)),
      {
        method: 'GET',
        headers: credentialsHeaders(credentials)
      }
    )
      .then(response => response)
      .then(toJson)
      .then(repairMessageMojibake)
      .catch(() => {
        // PEC-8747: el 401 ya pasó por la renovación de apiFetch; no se navega a /login ni se cierra sesión
        throw Error();
      })
      .finally(() => {
        dispatch(backendRequestCompleted());
      });
  };
}

/**
 * Performs a request to the BE to mark a message as seen
 * @param credentials
 * @param folder
 * @param message
 * @returns {Promise<Response>}
 * @private
 */
function _messageSeenRequest(credentials, folder, message) {
  return apiFetch(
    toApiProxyUrl(folder._links['message.seen'].href.replace('{messageId}', message.uid)),
    {
      method: 'PUT',
      headers: credentialsHeaders(credentials, {
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(true)
    }
  );
}

/**
 * Reads the content of a message.
 *
 * If the message is cached in the application.downloadedMessages map, the content is loaded from the cache
 * and will be completed with a BE request to fetch the missing parts (attachments...).
 *
 * If the message is not cached, a backend request will be performed to read the complete message.
 *
 * In both cases, any missing embedded image in tha attachment list will be fetched. For cached messages, unless action
 * was previously aborted, the embedded images will have already been loaded.
 *
 * BE read message request won't set the message as seen. If the BE request completes successfully, and additional
 * request to the server will be sent to confirm this and set the message as seen.
 *
 * @param dispatch
 * @param credentials
 * @param downloadedMessages
 * @param folder
 * @param message {object}
 */
export function readMessage(
  dispatch,
  credentials,
  downloadedMessages,
  folder,
  message,
  type = 'blob',
  markAsSeen = true
) {
  // Abort any operations that can affect operation result data consistency
  dispatch(backendRequest());
  closeResetFolderMessagesCacheEventSource(dispatch);
  abortFetch(abortControllerWrappers.getFoldersAbortController);
  const fetchMessage = _readMessageRequest(
    dispatch,
    credentials,
    folder,
    message
  );
  const signal = abortControllerWrappers.readMessageAbortController.signal;

  const downloadedMessage = downloadedMessages[message?.messageId];
  let $message;
  if (downloadedMessage) {
    // Read message from application.downloadedMessages cache
    // //////////////////////////////////////////////////////
    // Update cached/downloaded message (keep content), message may have been moved/read/...
    let updatedMessage = markAsSeen
      ? { ...message, folder: { ...folder }, seen: true }
      : { ...message, folder: { ...folder } };
    Object.entries(updatedMessage)
      .filter(entry =>
        // Remove null/undefined
        entry[1] === null || typeof entry[1] === 'undefined' ||
        // Remove empty strings/arrays only
        ((typeof entry[1] === 'string' || Array.isArray(entry[1])) && entry[1].length === 0)
      ) // Remove null/undefined and empty arrays/strings
      .forEach(([key]) => delete updatedMessage[key]);
    updatedMessage = Object.assign({ ...downloadedMessage }, updatedMessage);
    // Show optimistic version of updated downloadedMessage
    dispatch(refreshMessage(folder, updatedMessage));
    // Update folder message cache to set message as seen ASAP
    dispatch(updateCacheIfExist(folder, [updatedMessage]));
    // Update folder seen counter if applicable
    if (markAsSeen && !message.seen) {
      dispatch(
        updateFolder({
          ...folder,
          unreadMessageCount: folder.unreadMessageCount - 1
        })
      );
      // Send request to BE to mark message as read
      dispatch(backendRequest());
      _messageSeenRequest(credentials, folder, message)
        .finally(() => dispatch(backendRequestCompleted()));
    }
    // Read message from BE to update links (attachments) and other mutable properties
    $message = fetchMessage().then(completeMessage => {
      if (completeMessage === null) {
        // Aborted: stop chain so bottom catch swallows silently
        throw new Error('aborted');
      }
      return Promise.resolve({ ...completeMessage, content: updatedMessage.content });
    });
  } else {
    // Read message from BE (Set message seen only if request is successful)
    $message = fetchMessage()
      // Message successfully loaded, send signal to BE to mark as read if applicable and change folder information
      .then(completeMessage => {
        if (completeMessage === null) {
          // Aborted: stop chain so bottom catch swallows silently
          throw new Error('aborted');
        }
        if (markAsSeen && !completeMessage.seen) {
          dispatch(backendRequest());
          _messageSeenRequest(
            credentials,
            completeMessage.folder,
            completeMessage
          ).finally(() => dispatch(backendRequestCompleted()));
          completeMessage.folder.unreadMessageCount--;
        }
        return Promise.resolve(completeMessage);
      });
  }
  $message
    .then(completeMessage => {
      dispatch(selectFolder(completeMessage.folder));
      // Optimistically set message as seen
      if (markAsSeen) {
        completeMessage.seen = true;
      }
      // Update folder cache with message marked as read (don't store content in cache)
      const messageWithNoContent = { ...completeMessage };
      delete messageWithNoContent.content;
      dispatch(updateCacheIfExist(folder, [messageWithNoContent]));
      // Refresh message view
      dispatch(refreshMessage(folder, completeMessage));
       // Update folder with freshest BE information (and optimistic unreadMessageCount)
      dispatch(updateFolder(completeMessage.folder));
      // Read message's embedded images if used in message content
      completeMessage.attachments
        .filter(a => a.contentId && a.contentId.length > 0)
        .filter(a => a.contentType.toLowerCase().indexOf('image/') === 0)
        .filter(a => {
          const contentId = a.contentId.replace(/[<>]/g, '');
          return completeMessage.content.indexOf(`cid:${contentId}`) >= 0;
        })
        .forEach(a => {
          _readEmbeddedContent(
            dispatch,
            credentials,
            folder,
            message,
            signal,
            a,
            type
          );
        }
        );
      return completeMessage;
    })
    .finally(() => {
      dispatch(backendRequestCompleted());
    })
    .catch(() => {});
}

export function readMessageRaw(
  dispatch,
  credentials,
  downloadedMessages,
  folder,
  message
) {
  const fetchMessage = _readMessageRawRequest(
    dispatch,
    credentials,
    folder,
    message
  );

  const $message = fetchMessage().then(completeMessage => {
    if (completeMessage.unAuthorized) {
      return {unAuthorized: completeMessage.unAuthorized};
    }
    return Promise.resolve({ message, raw: base64url.decode(completeMessage) });
  });
  return $message;
}

export function readMessageFromUnloadedFolderByMessageId(
  dispatch,
  credentials,
  downloadedMessages,
  folder,
  message
) {
  const fetchMessage = _readMessageRawRequestByMessageId(
    dispatch,
    credentials,
    folder,
    message
  );
  const $message = fetchMessage().then(completeMessage => Promise.resolve({ completeMessage }));

  return $message;
}
